/**
 * Classification and reporting for v3 submission failures.
 *
 * `postV3Multipart` already surfaces what the backend said — it puts the
 * response body's `message`/`error` on `error.message` and the status on
 * `error.httpStatus`. Until now the document flows discarded both: every
 * failure landed on the same generic screen with a Retry button, the partner's
 * `onError` never fired, and nothing reached Sentry. A 403 "ID type not enabled
 * for this partner." was therefore invisible to the one person who could fix it,
 * while the end user was invited to retry bytes that could never succeed.
 *
 * Two audiences, deliberately different detail:
 *
 *   - The END USER gets a localised message for the class of failure. Backend
 *     strings are written for integrators and are English-only, so rendering
 *     them verbatim would show untranslated internal wording in a fr-FR or
 *     ar-EG session — and "ID type not enabled for this partner" is not
 *     something the person holding the phone can act on anyway.
 *   - The PARTNER gets the exact status and message through `onError` and
 *     Sentry, because that is who can actually change the configuration.
 */

/**
 * Whether retrying the same submission could plausibly succeed.
 *
 * Network/timeout failures and 5xx are transient. A 4xx is the server saying
 * this request is wrong — the images, id_type or account state won't change by
 * sending the identical payload again, so offering Retry there is a dead end.
 *
 * @param {Error & {httpStatus?: number, isNetworkError?: boolean}} error
 * @returns {boolean}
 */
export const isRetryableSubmissionError = (error) => {
  if (!error) return true;
  if (error.isNetworkError) return true;

  const status = error.httpStatus;
  // No status at all means it never reached the server (abort, DNS, CORS).
  if (typeof status !== 'number') return true;

  return status >= 500;
};

/**
 * Locale key for the message shown to the end user.
 *
 * Only non-retryable failures get a specific message; retryable ones keep the
 * existing failure screen and its Retry affordance.
 *
 * @param {Error & {httpStatus?: number}} error
 * @returns {string} a `document.submission.error.*` key
 */
export const submissionErrorMessageKey = (error) => {
  const status = error && error.httpStatus;

  // 401/403 are account/configuration problems — a disabled id_type, an
  // unauthorised product. Support (or the integrator) has to resolve them.
  if (status === 401 || status === 403) {
    return 'document.submission.error.notPermitted';
  }

  return 'document.submission.error.rejected';
};

/**
 * Stable failure-class identifiers for `onResult`'s `failure.error.error_code`.
 * Scoped to the failure classes that actually occur in this codebase today.
 */
export const ERROR_CODES = {
  CONFIG_INVALID: 'CONFIG_INVALID',
  SESSION_INIT_FAILED: 'SESSION_INIT_FAILED',
  CONSENT_DENIED: 'CONSENT_DENIED',
  SUBMISSION_FAILED: 'SUBMISSION_FAILED',
  NOT_PERMITTED: 'NOT_PERMITTED',
  DOCUMENTS_REJECTED: 'DOCUMENTS_REJECTED',
};

/**
 * Build the payload posted to the host page as `SmileIdentity::Error`.
 *
 * The parent's message handler reads `event.data.message || event.data`, so an
 * object with a `message` tag routes exactly like the bare string the init path
 * posts, and `onError` receives the whole object. Partners who previously got
 * nothing for submission failures now get the status and the backend's own
 * wording; nobody's existing string handling changes.
 *
 * @param {Error & {httpStatus?: number}} error
 * @param {string} product e.g. `enhanced_document_verification`
 * @returns {{message: string, product: string, status: number|null, error: string, retryable: boolean, error_code: string}}
 */
export const buildSubmissionErrorMessage = (error, product) => ({
  message: 'SmileIdentity::Error',
  product,
  status:
    error && typeof error.httpStatus === 'number' ? error.httpStatus : null,
  error: (error && error.message) || 'Submission failed',
  retryable: isRetryableSubmissionError(error),
  error_code:
    submissionErrorMessageKey(error) ===
    'document.submission.error.notPermitted'
      ? ERROR_CODES.NOT_PERMITTED
      : ERROR_CODES.SUBMISSION_FAILED,
});

/**
 * Build the payload posted to the host page as `SmileIdentity::Result`.
 *
 * Mirrors `buildSubmissionErrorMessage`: `message` is the routing
 * discriminator the parent switches on, so the job's own `message` field can
 * live inside `payload` without colliding.
 *
 * `payload` is the submission response passed through verbatim. For the v3
 * endpoints that is `{status, message, job_id, user_id, created_at}`; the
 * products still on a legacy endpoint (basic KYC and e-signature) have no v3
 * route to move to, so theirs is whatever that endpoint returns.
 *
 * @param {object} submission the resolved submission response
 * @param {string} product e.g. `biometric_kyc`
 * @returns {{message: string, product: string, payload: object}}
 */
export const buildSubmissionSuccessMessage = (submission, product) => ({
  message: 'SmileIdentity::Result',
  product,
  payload: submission,
});

/**
 * The three `onResult` envelope shapes.
 *
 * @param {string} product
 * @param {{job_id?: string, user_id?: string, status?: string, message?: string, created_at?: string}} value the resolved v3 submission response, passed through verbatim
 * @returns {{status: 'success', product: string, value: object}}
 */
export const buildResultSuccess = (product, value) => ({
  status: 'success',
  product,
  value,
});

/**
 * @param {string} product
 * @param {{error_code: string, message: string, status: number|null, retryable: boolean}} error
 * @returns {{status: 'failure', product: string, error: object}}
 */
export const buildResultFailure = (product, error) => ({
  status: 'failure',
  product,
  error,
});

/**
 * @param {string} product
 * @returns {{status: 'cancelled', product: string}}
 */
export const buildResultCancelled = (product) => ({
  status: 'cancelled',
  product,
});

/**
 * `onResult` failure for the sync `isConfigValid` throw in script.js, before
 * the iframe exists. This is the one failure class with a real, specific
 * `error.message` to surface.
 *
 * @param {Error} error
 * @param {string} product
 * @returns {{status: 'failure', product: string, error: object}}
 */
export const buildConfigInvalidFailure = (error, product) =>
  buildResultFailure(product, {
    error_code: ERROR_CODES.CONFIG_INVALID,
    message: error?.message,
    status: null,
    retryable: false,
  });

/**
 * `onResult` failure for an exhausted session-init retry loop. The wire
 * message is the bare `'SmileIdentity::Error'` string (the `onError`
 * contract), so the caught error only reaches Sentry — this carries a
 * generic message rather than threading it through.
 *
 * @param {string} product
 * @returns {{status: 'failure', product: string, error: object}}
 */
export const buildSessionInitFailedFailure = (product) =>
  buildResultFailure(product, {
    error_code: ERROR_CODES.SESSION_INIT_FAILED,
    message: 'Failed to initialize session',
    status: null,
    retryable: true,
  });

/**
 * `onResult` failure for both consent-denial wire tags — the plain
 * `'SmileIdentity::ConsentDenied'` and the TOTP contact-methods-outdated
 * variant map to the same `CONSENT_DENIED` error_code: both tags are one
 * failure class.
 *
 * @param {string} tag the raw wire tag `onError` received
 * @param {string} product
 * @returns {{status: 'failure', product: string, error: object}}
 */
export const buildConsentDeniedFailure = (tag, product) =>
  buildResultFailure(product, {
    error_code: ERROR_CODES.CONSENT_DENIED,
    message:
      tag === 'SmileIdentity::ConsentDenied::TOTP::ContactMethodsOutdated'
        ? 'Consent was not granted: contact methods are outdated'
        : 'Consent was not granted',
    status: null,
    retryable: false,
  });

/**
 * `onResult` failure for e-signature's `handleBadDocuments` — nothing was
 * submitted yet at that point, so this gets its own error_code rather than
 * reusing `SUBMISSION_FAILED`.
 *
 * @param {{data?: {error?: {message?: string}}}} error the wire object
 * `handleBadDocuments` posts (`{message, data: {error}, error_code}`)
 * @param {string} product
 * @returns {{status: 'failure', product: string, error: object}}
 */
export const buildDocumentsRejectedFailure = (error, product) =>
  buildResultFailure(product, {
    error_code: ERROR_CODES.DOCUMENTS_REJECTED,
    message: error?.data?.error?.message || 'Documents were rejected',
    status: null,
    retryable: false,
  });

/**
 * `onResult` failure for `script.js`'s `handleError`, which receives
 * whatever `'SmileIdentity::Error'` carried on the wire — a bare string
 * (session-init exhaustion, from five products), e-signature's
 * `DOCUMENTS_REJECTED` object, or the `buildSubmissionErrorMessage` object
 * (doc-verification/EDV backend rejections, which already carries its own
 * `error_code`). Dispatches on shape so `script.js` doesn't have to.
 *
 * @param {string|object} error the raw wire payload `onError` received
 * @param {string} product
 * @returns {{status: 'failure', product: string, error: object}}
 */
export const buildFailureResultFromError = (error, product) => {
  if (typeof error === 'string') {
    return buildSessionInitFailedFailure(product);
  }
  if (error?.error_code === ERROR_CODES.DOCUMENTS_REJECTED) {
    return buildDocumentsRejectedFailure(error, product);
  }
  return buildResultFailure(product, {
    error_code: error?.error_code || ERROR_CODES.SUBMISSION_FAILED,
    message: error?.error,
    status: error?.status ?? null,
    retryable: error?.retryable ?? false,
  });
};
